import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams, useNavigate } from 'react-router-dom';
import { teamSettingsSchema, TeamSettingsFormValues } from '../../lib/validations/teamSchema';
import { useTeamStore } from '../../lib/store/teamStore';
import Alert from '../common/Alert';
import Button from '../common/Button';
import Card from '../common/Card';
import Input from '../common/Input';

export const TeamSettingsForm = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { currentTeam, isLoading, error, setCurrentTeam, updateTeam } = useTeamStore();
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm<TeamSettingsFormValues>({
    resolver: zodResolver(teamSettingsSchema),
    defaultValues: {
      name: '',
      shifts_per_provider: 13,
      day_providers_required: 2,
      night_providers_required: 1,
      scheduling_period_days: 28,
      max_consecutive_shifts: 5,
      min_rest_hours: 10
    }
  });
  
  useEffect(() => {
    if (teamId) {
      setCurrentTeam(teamId);
    }
  }, [teamId, setCurrentTeam]);
  
  useEffect(() => {
    if (currentTeam) {
      reset({
        name: currentTeam.name,
        shifts_per_provider: currentTeam.shifts_per_provider,
        day_providers_required: currentTeam.day_providers_required,
        night_providers_required: currentTeam.night_providers_required,
        scheduling_period_days: currentTeam.scheduling_period_days,
        max_consecutive_shifts: currentTeam.max_consecutive_shifts,
        min_rest_hours: currentTeam.min_rest_hours
      });
    }
  }, [currentTeam, reset]);
  
  const onSubmit = async (data: TeamSettingsFormValues) => {
    if (!teamId) return;
    
    setIsSubmitting(true);
    setUpdateError(null);
    setSuccessMessage(null);
    
    try {
      await updateTeam(teamId, data);
      setSuccessMessage('Team settings updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Failed to update team. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <Alert variant="error" className="mb-4">
        {error}
      </Alert>
    );
  }
  
  if (!currentTeam) {
    return (
      <Alert variant="error" className="mb-4">
        Team not found
      </Alert>
    );
  }
  
  return (
    <Card className="max-w-2xl mx-auto p-6">
      <h2 className="text-2xl font-semibold mb-6">Team Settings</h2>
      
      {updateError && (
        <Alert variant="error" className="mb-4">
          {updateError}
        </Alert>
      )}
      
      {successMessage && (
        <Alert variant="success" className="mb-4">
          {successMessage}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <Input
            label="Team Name"
            {...register('name')}
            error={errors.name?.message}
            required
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            type="number"
            label="Shifts Per Provider"
            {...register('shifts_per_provider', { valueAsNumber: true })}
            error={errors.shifts_per_provider?.message}
            required
          />
          
          <Input
            type="number"
            label="Scheduling Period (Days)"
            {...register('scheduling_period_days', { valueAsNumber: true })}
            error={errors.scheduling_period_days?.message}
            required
          />
          
          <Input
            type="number"
            label="Day Providers Required"
            {...register('day_providers_required', { valueAsNumber: true })}
            error={errors.day_providers_required?.message}
            required
          />
          
          <Input
            type="number"
            label="Night Providers Required"
            {...register('night_providers_required', { valueAsNumber: true })}
            error={errors.night_providers_required?.message}
            required
          />
          
          <Input
            type="number"
            label="Max Consecutive Shifts"
            {...register('max_consecutive_shifts', { valueAsNumber: true })}
            error={errors.max_consecutive_shifts?.message}
            required
          />
          
          <Input
            type="number"
            label="Minimum Rest Hours"
            {...register('min_rest_hours', { valueAsNumber: true })}
            error={errors.min_rest_hours?.message}
            required
          />
        </div>
        
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/teams/${teamId}/dashboard`)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
          >
            Save Settings
          </Button>
        </div>
      </form>
    </Card>
  );
}; 